import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Select,
  Stack,
  Switch,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedFormControlLabel as FormControlLabel, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import { STUDIO_API_BASE_URL } from '../../api/studioClient';

const EVENTS = [
  ['marriage', 'Marriage'], ['childbirth', 'Childbirth / Progeny'],
  ['job', 'New Job / Employment'], ['promotion', 'Promotion / Career Rise'],
  ['business', 'Business Start / Expansion'], ['higherEducation', 'Higher Education'],
  ['property', 'Property Purchase / Construction'], ['vehicle', 'Vehicle Purchase'],
  ['foreignTravel', 'Foreign Travel'], ['foreignSettlement', 'Foreign Settlement'],
] as const;
const ymd = (date: Date) => date.toISOString().slice(0, 10);
type Props = { chartId?: string | null; chartData?: any; token?: string | null };

export default function DoubleTransitPanel({ chartId, chartData, token }: Props) {
  const today = React.useMemo(() => new Date(), []);
  const end = React.useMemo(() => { const value = new Date(today); value.setFullYear(value.getFullYear() + 5); return value; }, [today]);
  const [event, setEvent] = React.useState('marriage');
  const [mode, setMode] = React.useState<'strict' | 'network'>('strict');
  const [from, setFrom] = React.useState(ymd(today));
  const [to, setTo] = React.useState(ymd(end));
  const [useMoonLagna, setUseMoonLagna] = React.useState(true);
  const [useSunLagna, setUseSunLagna] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<any>(null);

  const calculate = async () => {
    if (!chartId && !chartData?.planets) return setError('Select or calculate a chart first.');
    try {
      setLoading(true); setError(null);
      const response = await fetch(`${STUDIO_API_BASE_URL}/transits/double`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          ...(chartId ? { chartId } : { chartData }), event, from, to, mode,
          useMoonLagna, useVarga: false, useSunLagna, dashaMode: 'parashari',
        }),
      });
      const json = await response.json();
      if (!response.ok || json.success === false) throw new Error(json.message || 'Double transit calculation failed');
      setResult(json.data);
    } catch (calculationError: any) {
      setError(calculationError.message || 'Double transit calculation failed');
    } finally { setLoading(false); }
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={800}>Guru–Shani Double Transit</Typography>
          <Typography color="text.secondary" sx={{ mt: .75, mb: 2 }}>
            Find periods when Jupiter and Saturn jointly activate the selected life-event factors. These are activation windows, not guaranteed events.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth><InputLabel>Life event</InputLabel><Select value={event} label="Life event" onChange={(e) => setEvent(e.target.value)}>
              {EVENTS.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </Select></FormControl>
            <FormControl fullWidth><InputLabel>Activation mode</InputLabel><Select value={mode} label="Activation mode" onChange={(e) => setMode(e.target.value as 'strict' | 'network')}>
              <MenuItem value="strict">Strict Double Transit</MenuItem><MenuItem value="network">Supporting Double Activation</MenuItem>
            </Select></FormControl>
            <TextField fullWidth type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 2 }}>
            <FormControlLabel control={<Switch checked disabled />} label="Lagna (D1)" />
            <FormControlLabel control={<Switch checked={useMoonLagna} onChange={(e) => setUseMoonLagna(e.target.checked)} />} label="Chandra Lagna" />
            <FormControlLabel control={<Switch checked={useSunLagna} onChange={(e) => setUseSunLagna(e.target.checked)} />} label="Surya Lagna" />
          </Stack>
          <Button variant="contained" disabled={loading} onClick={calculate} sx={{ mt: 1.5 }}>
            {loading ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Calculating…</> : 'Find activation windows'}
          </Button>
        </CardContent>
      </Card>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {result?.count === 0 ? <Alert severity="info">No matching windows were found. Try Supporting Double Activation or a wider range.</Alert> : null}
      <Stack spacing={2}>
        {(result?.windows || []).map((window: any, index: number) => (
          <Card key={`${window.startISO}-${window.endISO}-${index}`} variant="outlined" sx={{ borderRadius: 3 }}><CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
              <Box><Typography variant="h6" fontWeight={800}>Event: {window.eventLabel || EVENTS.find(([key]) => key === event)?.[1]}</Typography>
              <Typography color="text.secondary">{window.startISO} – {window.endISO} · {window.activationType === 'strict' ? 'Strict Double Transit' : 'Supporting Double Activation'}</Typography></Box>
              <Stack direction="row" gap={1}><Chip color={window.grade === 'A' ? 'success' : window.grade === 'B' ? 'warning' : 'default'} label={`Grade ${window.grade} · ${window.score}`} />
              {window.dashaSupported ? <Chip color="secondary" label="Dasha supported" /> : null}</Stack>
            </Stack>
            <Typography sx={{ mt: 1.5, fontWeight: 800 }}>Comments</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{window.comments || window.note || 'Confirmed by Lagna (D1).'}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>{window.disclaimer}</Typography>
          </CardContent></Card>
        ))}
      </Stack>
    </Box>
  );
}
